import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { RefreshCw, TrendingUp, Building2, ShieldCheck, DollarSign, FileCheck2, AlertTriangle } from "lucide-react";

/**
 * Executive Reporting Foundation — asset-agnostic aggregation dashboard.
 * Board-pack shaped data. NO automated email delivery (per product policy).
 */
export default function ExecutiveReports() {
  const [period, setPeriod] = useState("monthly");
  const [country, setCountry] = useState("");
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const params = { period };
      if (country) params.country = country;
      const r = await api.get("/reports/executive-summary", { params });
      setD(r.data);
    } catch (e) { console.error(e); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period]);

  const k = d?.kpis || {};

  return (
    <div>
      <Navbar />
      <div className="max-w-[1500px] mx-auto px-6 py-10">
        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="kicker">Executive · Reporting Foundation</div>
            <h1 className="display text-3xl">Board-pack Executive Summary</h1>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">Aggregation only. Asset-agnostic across all verification lines. Automated distribution is disabled by policy — export or share manually.</p>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger data-testid="report-period"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="weekly">Last 7 days</SelectItem>
                <SelectItem value="monthly">Last 30 days</SelectItem>
                <SelectItem value="quarterly">Last 90 days</SelectItem>
                <SelectItem value="ytd">Year to date</SelectItem>
              </SelectContent>
            </Select>
            <Input placeholder="Country (blank = Africa-wide)" value={country} onChange={(e) => setCountry(e.target.value)} onBlur={load} className="h-9 w-56" data-testid="report-country" />
            <Button variant="outline" size="sm" onClick={load} disabled={busy} data-testid="report-refresh"><RefreshCw className="h-3.5 w-3.5 mr-1" />Refresh</Button>
            <Link to="/ops"><Button size="sm" variant="outline">Ops Centre</Button></Link>
          </div>
        </div>

        {!d && <div className="text-sm text-muted-foreground">Loading executive summary…</div>}
        {d && (
          <>
            <div className="mono text-[10.5px] uppercase tracking-widest text-muted-foreground mb-4">
              Scope: {d.country_scope} · {d.period_label} · generated {new Date(d.generated_at).toLocaleString()}
            </div>

            {/* Highlights */}
            <div className="border border-border bg-card p-5 mb-6">
              <div className="kicker">Executive highlights</div>
              <ul className="mt-3 space-y-1.5 text-sm">
                {(d.narrative_highlights || []).map((n, i) => (
                  <li key={i} className="flex items-start gap-2" data-testid={`report-highlight-${i}`}>
                    <span className="mono text-primary mt-0.5">→</span><span>{n}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* KPI grid */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
              <KPI label="Verification requests" value={k.verification_requests_total} sub={`${k.verification_completed} completed · ${k.verification_priority} priority`} Icon={FileCheck2} />
              <KPI label="Completion rate" value={`${k.verification_completion_rate || 0}%`} Icon={ShieldCheck} accent="text-emerald-700" />
              <KPI label="Escrow value released" value={`$${(k.escrow_value_completed_usd || 0).toLocaleString()}`} sub={`${k.escrow_completed} of ${k.escrow_transactions_total} released`} Icon={DollarSign} accent="text-primary" />
              <KPI label="Escrow value active" value={`$${(k.escrow_value_active_usd || 0).toLocaleString()}`} sub={`${k.escrow_disputed} disputed · ${k.escrow_refunded} refunded`} Icon={TrendingUp} />
              <KPI label="Certificates issued" value={k.certificates_issued} sub={`${k.certificates_valid} valid · ${k.certificates_revoked} revoked`} Icon={ShieldCheck} />
              <KPI label="Trusted updates" value={k.trusted_updates} sub={`${k.passport_events} passport events`} Icon={Building2} />
              <KPI label="Audit events" value={k.audit_events} sub={`${k.config_changes} config changes`} Icon={AlertTriangle} />
              <KPI label="New users" value={k.users_new} sub={`${k.users_active} active total`} Icon={Building2} />
            </div>

            {/* Distributions */}
            <div className="grid lg:grid-cols-3 gap-4 mb-6">
              <Panel title="Top verification categories" data={d.categories} testid="report-categories" />
              <Panel title="Top verification industries" data={d.industries} testid="report-industries" />
              <Panel title="Escrow by asset type" data={(d.escrow_by_asset_type || []).map(x => ({ label: x.asset_type, count: x.count }))} testid="report-escrow-asset" />
            </div>

            {(!country && (d.country_breakdown || []).length > 0) && (
              <div className="border border-border bg-card p-5 mb-6">
                <div className="kicker">Country breakdown</div>
                <div className="mt-3 space-y-1.5">
                  {(d.country_breakdown || []).map(c => (
                    <div key={c.country} className="flex items-center justify-between text-[12.5px]" data-testid={`report-country-${c.country}`}>
                      <span>{c.country}</span>
                      <span className="mono text-[11px] text-muted-foreground">{c.count} requests</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mono text-[10.5px] uppercase tracking-widest text-muted-foreground pt-2 border-t border-border">
              {d.footer?.note}
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}

const KPI = ({ label, value, sub, Icon, accent }) => (
  <div className="border border-border bg-card p-4">
    <div className="flex items-start justify-between">
      <div>
        <div className="kicker">{label}</div>
        <div className={`display text-2xl mt-1 ${accent || ""}`}>{value ?? 0}</div>
        {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
      </div>
      {Icon && <Icon className="h-4 w-4 text-primary" />}
    </div>
  </div>
);

const Panel = ({ title, data, testid }) => (
  <div className="border border-border bg-card p-5" data-testid={testid}>
    <div className="kicker">{title}</div>
    <div className="mt-3 space-y-1.5">
      {(data || []).length === 0 && <div className="text-xs text-muted-foreground">No data.</div>}
      {(data || []).map((r, i) => (
        <div key={i} className="flex items-center justify-between text-[12.5px]">
          <span className="capitalize">{(r.label || "").replace(/_/g, ' ')}</span>
          <span className="mono text-[11px] text-muted-foreground">{r.count}</span>
        </div>
      ))}
    </div>
  </div>
);
